// src/components/forms/TenancyActions.tsx — the moves a tenancy can make.
//
// A tenancy is not a form. It is created by converting an application and then moves
// through activate → renew → terminate, each of which does real work: activation
// generates the entire rent schedule and the deposit with its protection deadline.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Button, Field, Input, Modal, Select, Textarea } from '../ui';
import { FormError, FormGrid, Wide, numberOr, toMajor, toMinor } from './common';

type T = Record<string, any>;
const today = () => new Date().toISOString().slice(0, 10);
const addMonths = (iso: string, months: number) => {
  const d = new Date(iso); d.setMonth(d.getMonth() + months); d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const useAfter = (id: string, onDone?: () => void) => {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['tenancy', id] });
    void qc.invalidateQueries({ queryKey: ['/tenancies'] });
    void qc.invalidateQueries({ queryKey: ['/rent-schedule'] });
    void qc.invalidateQueries({ queryKey: ['tenancy-deposits', id] });
    void qc.invalidateQueries({ queryKey: ['/deposits'] });
    onDone?.();
  };
};

/** Activation is the moment the tenancy starts costing and earning money. */
export function ActivateTenancy({ open, onClose, tenancy }: { open: boolean; onClose: () => void; tenancy: T }) {
  const after = useAfter(tenancy.id, onClose);
  const [form, setForm] = useState({ moveInDate: tenancy.startDate ?? today(), depositReceivedOn: today(), generateSchedule: true });
  const run = useMutation({
    mutationFn: async () => api.post(`/tenancies/${tenancy.id}/activate`, {
      moveInDate: form.moveInDate || undefined,
      depositReceivedOn: form.depositReceivedOn || undefined,
      generateSchedule: form.generateSchedule,
    }),
    onSuccess: after,
  });
  return (
    <Modal open={open} onClose={onClose} title="Activate this tenancy"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => run.mutate()} disabled={run.isPending}>{run.isPending ? 'Activating…' : 'Activate'}</Button></>}>
      <div className="space-y-5">
        <p className="text-[13.5px] leading-relaxed text-ink-600">
          This raises the whole rent schedule from the start date and opens the deposit record. In England the protection clock starts the day the deposit is received — thirty days, and the penalty for missing it is one to three times the deposit.
        </p>
        <FormGrid>
          <Field label="Move-in date"><Input type="date" value={form.moveInDate} onChange={(e) => setForm({ ...form, moveInDate: e.target.value })} className="w-full" /></Field>
          <Field label="Deposit received on" hint="Starts the 30-day protection deadline"><Input type="date" value={form.depositReceivedOn} onChange={(e) => setForm({ ...form, depositReceivedOn: e.target.value })} className="w-full" /></Field>
          <Wide>
            <label className="flex items-start gap-2.5 text-[13px] text-ink-700">
              <input type="checkbox" checked={form.generateSchedule} onChange={(e) => setForm({ ...form, generateSchedule: e.target.checked })} className="mt-0.5 h-4 w-4 accent-ink-900" />
              <span>Generate the rent schedule<span className="mt-0.5 block text-[11.5px] text-ink-500">Leave this off only when rent is being migrated in from another system.</span></span>
            </label>
          </Wide>
        </FormGrid>
        <FormError error={run.error} />
      </div>
    </Modal>
  );
}

/** Renewal rebuilds the remaining schedule at the new rent. */
export function RenewTenancy({ open, onClose, tenancy }: { open: boolean; onClose: () => void; tenancy: T }) {
  const after = useAfter(tenancy.id, onClose);
  const [form, setForm] = useState({
    termMonths: '12', endDate: '', newRent: toMajor(tenancy.rentMinor), effectiveFrom: tenancy.endDate ?? today(), mode: 'term' as 'term' | 'date',
  });
  const run = useMutation({
    mutationFn: async () => api.post(`/tenancies/${tenancy.id}/renew`, {
      ...(form.mode === 'term' ? { termMonths: numberOr(form.termMonths, 12) } : { endDate: form.endDate || null }),
      newRentMinor: toMinor(form.newRent),
      effectiveFrom: form.effectiveFrom || undefined,
    }),
    onSuccess: after,
  });
  const rise = (toMinor(form.newRent) ?? 0) - Number(tenancy.rentMinor ?? 0);
  const risePct = tenancy.rentMinor ? (rise / Number(tenancy.rentMinor)) * 100 : 0;
  return (
    <Modal open={open} onClose={onClose} title="Renew this tenancy"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => run.mutate()} disabled={run.isPending}>{run.isPending ? 'Renewing…' : 'Renew'}</Button></>}>
      <div className="space-y-5">
        <FormGrid>
          <Wide>
            <Field label="New term">
              <div className="flex gap-1.5">
                <button type="button" onClick={() => setForm({ ...form, mode: 'term' })} className={`rounded-full border px-3 py-1.5 text-[12px] transition ${form.mode === 'term' ? 'border-ink-900 bg-ink-900 text-white' : 'border-ink-200 bg-white text-ink-600'}`}>For a number of months</button>
                <button type="button" onClick={() => setForm({ ...form, mode: 'date' })} className={`rounded-full border px-3 py-1.5 text-[12px] transition ${form.mode === 'date' ? 'border-ink-900 bg-ink-900 text-white' : 'border-ink-200 bg-white text-ink-600'}`}>To a fixed date</button>
              </div>
            </Field>
          </Wide>
          {form.mode === 'term'
            ? <Field label="Months" hint={form.effectiveFrom ? `Runs to ${addMonths(form.effectiveFrom, numberOr(form.termMonths, 12) ?? 12)}` : undefined}><Input value={form.termMonths} onChange={(e) => setForm({ ...form, termMonths: e.target.value })} inputMode="numeric" className="w-full" /></Field>
            : <Field label="New end date" hint="Blank makes it open periodic"><Input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} className="w-full" /></Field>}
          <Field label="Effective from"><Input type="date" value={form.effectiveFrom} onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })} className="w-full" /></Field>
          <Wide>
            <Field label={`New rent (${tenancy.rentCurrency ?? 'GBP'} per ${String(tenancy.rentPeriod ?? 'MONTH').toLowerCase()})`}
              hint={rise !== 0 ? `${rise > 0 ? 'Increase' : 'Reduction'} of ${Math.abs(risePct).toFixed(1)}% on the current rent` : 'Same as now'}>
              <Input value={form.newRent} onChange={(e) => setForm({ ...form, newRent: e.target.value })} inputMode="decimal" className="w-full" />
            </Field>
          </Wide>
        </FormGrid>
        <p className="text-[12px] leading-relaxed text-ink-400">Unpaid instalments from the effective date are rebuilt at the new rent. Anything already paid is left alone.</p>
        <FormError error={run.error} />
      </div>
    </Modal>
  );
}

/** Termination records who gave notice — which decides the notice period. */
export function TerminateTenancy({ open, onClose, tenancy }: { open: boolean; onClose: () => void; tenancy: T }) {
  const after = useAfter(tenancy.id, onClose);
  const notice = Number(tenancy.noticePeriodDays ?? 60);
  const earliest = new Date(Date.now() + notice * 86_400_000).toISOString().slice(0, 10);
  const [form, setForm] = useState({ noticeBy: 'TENANT', noticeGivenOn: today(), requestedEndDate: earliest, reason: '' });
  const run = useMutation({
    mutationFn: async () => api.post(`/tenancies/${tenancy.id}/terminate`, {
      noticeBy: form.noticeBy, noticeGivenOn: form.noticeGivenOn || undefined,
      requestedEndDate: form.requestedEndDate || undefined, reason: form.reason.trim() || undefined,
    }),
    onSuccess: after,
  });
  return (
    <Modal open={open} onClose={onClose} title="Give notice on this tenancy"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button variant="danger" onClick={() => run.mutate()} disabled={run.isPending}>{run.isPending ? 'Recording…' : 'Record notice'}</Button></>}>
      <div className="space-y-5">
        <FormGrid>
          <Field label="Notice given by"><Select value={form.noticeBy} onChange={(e) => setForm({ ...form, noticeBy: e.target.value })} className="w-full"><option value="TENANT">The tenant</option><option value="LANDLORD">Lotus</option></Select></Field>
          <Field label="Given on"><Input type="date" value={form.noticeGivenOn} onChange={(e) => setForm({ ...form, noticeGivenOn: e.target.value })} className="w-full" /></Field>
          <Wide><Field label="Requested end date" hint={`The notice period on this tenancy is ${notice} days, so the earliest is ${earliest}`}><Input type="date" value={form.requestedEndDate} onChange={(e) => setForm({ ...form, requestedEndDate: e.target.value })} className="w-full" /></Field></Wide>
          <Wide><Field label="Reason"><Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full" /></Field></Wide>
        </FormGrid>
        <p className="text-[12px] leading-relaxed text-ink-400">The tenancy moves to notice given, not ended — rent keeps running until the last day and the deposit stays held until it is returned.</p>
        <FormError error={run.error} />
      </div>
    </Modal>
  );
}

/** The few terms that may be corrected while a tenancy runs. */
export function EditTenancy({ open, onClose, tenancy }: { open: boolean; onClose: () => void; tenancy: T }) {
  const after = useAfter(tenancy.id, onClose);
  const [form, setForm] = useState({
    rent: toMajor(tenancy.rentMinor), rentDueDay: String(tenancy.rentDueDay ?? 1),
    noticePeriodDays: String(tenancy.noticePeriodDays ?? 60), endDate: tenancy.endDate ?? '',
    ejariNumber: tenancy.ejariNumber ?? '',
  });
  const run = useMutation({
    mutationFn: async () => api.patch(`/tenancies/${tenancy.id}`, {
      rentMinor: toMinor(form.rent), rentDueDay: numberOr(form.rentDueDay, 1),
      noticePeriodDays: numberOr(form.noticePeriodDays, 60), endDate: form.endDate || null,
      ejariNumber: form.ejariNumber.trim() || null,
    }),
    onSuccess: after,
  });
  return (
    <Modal open={open} onClose={onClose} title="Edit tenancy terms"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => run.mutate()} disabled={run.isPending}>{run.isPending ? 'Saving…' : 'Save changes'}</Button></>}>
      <div className="space-y-5">
        <FormGrid>
          <Field label={`Rent (${tenancy.rentCurrency ?? 'GBP'})`}><Input value={form.rent} onChange={(e) => setForm({ ...form, rent: e.target.value })} inputMode="decimal" className="w-full" /></Field>
          <Field label="Due on day" hint="1–28"><Input value={form.rentDueDay} onChange={(e) => setForm({ ...form, rentDueDay: e.target.value })} inputMode="numeric" className="w-full" /></Field>
          <Field label="Notice period (days)"><Input value={form.noticePeriodDays} onChange={(e) => setForm({ ...form, noticePeriodDays: e.target.value })} inputMode="numeric" className="w-full" /></Field>
          <Field label="End date" hint="Blank is open periodic"><Input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} className="w-full" /></Field>
          {tenancy.jurisdiction === 'AE' && <Wide><Field label="Ejari number" hint="UAE tenancy registration"><Input value={form.ejariNumber} onChange={(e) => setForm({ ...form, ejariNumber: e.target.value })} className="w-full" /></Field></Wide>}
        </FormGrid>
        <p className="text-[12px] leading-relaxed text-ink-400">Changing the rent here does not rebuild the schedule — use Renew for a rent review so the instalments follow.</p>
        <FormError error={run.error} />
      </div>
    </Modal>
  );
}
